import { Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PDFPreviewProps {
  pdfUrl?: string;
  pdfBlob?: Blob;
  title?: string;
  className?: string;
  showDownload?: boolean;
  onDownload?: () => void;
}

export const PDFPreview: React.FC<PDFPreviewProps> = ({
  pdfUrl,
  pdfBlob,
  title,
  className = '',
  showDownload = true,
  onDownload,
}) => {
  const { t } = useTranslation();
  const displayTitle = title || t('common.documentPreview');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    if (pdfBlob) {
      // Créer une URL objet depuis le Blob
      const url = URL.createObjectURL(pdfBlob);
      setObjectUrl(url);
      setLoading(false);

      // Cleanup
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (pdfUrl) {
      // Utiliser l'URL fournie
      setObjectUrl(pdfUrl);
      setLoading(false);
    } else {
      setError(t('common.noPdfProvided'));
      setLoading(false);
    }
  }, [pdfBlob, pdfUrl]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 25, 50));
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (objectUrl) {
      // Download par défaut
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${displayTitle}.pdf`;
      link.click();
    }
  };

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 rounded-lg border border-gray-300 ${className}`}
      >
        <div className="text-center p-8">
          <p className="text-red-600 font-medium mb-2">{t('common.loadError')}</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}>
      {/* Header - Responsive */}
      <div className="flex items-center justify-between px-2 md:px-4 py-2 md:py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <h3 className="text-sm md:text-base font-medium text-gray-900 truncate">{displayTitle}</h3>
          {loading && (
            <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
          )}
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Zoom Controls - Hidden on mobile */}
          <div className="hidden md:flex items-center gap-1 border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 50}
              className="p-1.5 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('common.zoomOut')}
            >
              <ZoomOut className="w-4 h-4 text-gray-700" />
            </button>
            <span className="px-2 text-sm font-medium text-gray-700 border-x border-gray-300">
              {zoom}%
            </span>
            <button
              onClick={handleZoomIn}
              disabled={zoom >= 200}
              className="p-1.5 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('common.zoomIn')}
            >
              <ZoomIn className="w-4 h-4 text-gray-700" />
            </button>
          </div>

          {/* Download Button - Icon only on mobile */}
          {showDownload && (
            <button
              onClick={handleDownload}
              disabled={!objectUrl}
              className="flex items-center gap-2 px-2 md:px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('common.actions.download')}
            >
              <Download className="w-4 h-4 text-gray-700" />
              <span className="hidden md:inline text-sm font-medium text-gray-700">{t('common.actions.download')}</span>
            </button>
          )}
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 bg-gray-100 relative overflow-auto">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        )}

        {objectUrl && (
          <iframe
            src={`${objectUrl}#zoom=${zoom}`}
            className="w-full h-full min-h-[400px] md:min-h-[600px]"
            title={displayTitle}
            onLoad={() => setLoading(false)}
            onError={() => {
              setError(t('common.cannotLoadPdf'));
              setLoading(false);
            }}
          />
        )}
      </div>
    </div>
  );
};
